import { Experiment } from '@ms/yammer-data/dist/state/types';
import { useIsFoundationalPillarsNavPhase1Enabled } from '@ms/yammer-features-app-navigation';
import {
  useExperimentTreatmentWithoutReporting,
  useReportExperimentTreatmentCallback,
} from '@ms/yammer-hooks/dist/experiments';
import { useIsTopLevelFeaturePage } from '@ms/yammer-hooks/dist/navigation';
import { useTranslations } from '@ms/yammer-i18n-react';
import CommonStrings from '@ms/yammer-strings/dist/Common';
import PanesStrings from '@ms/yammer-strings/dist/Panes';
import {
  rebrandedWebMainAreaHeroBackgroundDarkPath,
  rebrandedWebMainAreaHeroBackgroundPath,
} from '@ms/yammer-web-components/dist/images';
import {
  AppLayoutLoadableWeb,
  AppLayoutVivaEngage,
  AppLayoutVivaEngageProps,
} from '@ms/yammer-web-core-ui/dist/components/AppLayout';
import { useTheme } from '@ms/yammer-web-core-ui/dist/util/theme';
import React, { FC, Suspense } from 'react';

import { VivaAppBar } from '../VivaAppbar';

export type AppLayoutProps = Pick<
  AppLayoutVivaEngageProps,
  'backgroundColor' | 'header' | 'subHeader' | 'leftNav' | 'banner' | 'footer'
>;

const AppLayout: FC<AppLayoutProps> = ({ children, backgroundColor, header, subHeader, leftNav, banner, footer }) => {
  const openAriaLabel = useTranslations(PanesStrings).showAdditionalInformationPane();
  const closeAriaLabel = useTranslations(CommonStrings).commonClose();
  const isFoundationalPillarsNavPhase1Enabled = useIsFoundationalPillarsNavPhase1Enabled();

  const vivaAppBarExperimentTreatment = useExperimentTreatmentWithoutReporting(Experiment.VivaAppBar);
  const isVivaAppBarEnabled = !!vivaAppBarExperimentTreatment;
  const reportVivaAppBarExperimentTreatment = useReportExperimentTreatmentCallback(
    Experiment.VivaAppBar,
    vivaAppBarExperimentTreatment
  );
  if (isFoundationalPillarsNavPhase1Enabled) {
    reportVivaAppBarExperimentTreatment();
  }

  const isTopLevelFeaturePage = useIsTopLevelFeaturePage();
  const { isInverted } = useTheme();

  const rebrandedHeroBannerImgPath = isInverted
    ? rebrandedWebMainAreaHeroBackgroundDarkPath
    : rebrandedWebMainAreaHeroBackgroundPath;

  const heroBannerImgSrc = isTopLevelFeaturePage ? rebrandedHeroBannerImgPath : undefined;

  const outerLeftNav = isVivaAppBarEnabled ? <VivaAppBar /> : undefined;

  return isFoundationalPillarsNavPhase1Enabled ? (
    <Suspense fallback={null}>
      <AppLayoutVivaEngage
        header={header}
        banner={banner}
        subHeader={subHeader}
        bannerImageSrc={heroBannerImgSrc}
        isNewNavRedesign={true}
        outerLeftNav={outerLeftNav}
        leftNav={leftNav}
        openContentSidebarAriaLabel={openAriaLabel}
        closeContentSidebarAriaLabel={closeAriaLabel}
        footer={footer}
        backgroundColor={backgroundColor}
      >
        {children}
      </AppLayoutVivaEngage>
    </Suspense>
  ) : (
    <AppLayoutLoadableWeb
      header={header}
      banner={banner}
      subHeader={subHeader}
      mainAreaBackgroundSrc={heroBannerImgSrc}
      leftNav={leftNav}
      openContentSidebarAriaLabel={openAriaLabel}
      closeContentSidebarAriaLabel={closeAriaLabel}
      footer={footer}
      backgroundColor={backgroundColor}
    >
      {children}
    </AppLayoutLoadableWeb>
  );
};

export default AppLayout;
